import { evaluateRn247MonthlyReportTenantAware } from '../compliance-deadlines/rn-24-7';
import type { ComplianceDeadlineItem } from '../compliance-deadlines/types';
import type { ComplianceCalendarApplicability, GenerateRecurringObligationsInput } from './types';
import { addCalendarDays } from './date-only';

const UNKNOWN_APPLICABILITY_HINT = 'Applicability not confirmed for your provider profile';

function shouldShow(value: ComplianceCalendarApplicability): boolean {
  return value !== false;
}

function statutoryItem(
  id: string,
  title: string,
  dueAt: string,
  provision: string,
  applies: ComplianceCalendarApplicability,
): ComplianceDeadlineItem {
  return {
    id,
    register: 'RECURRING_ORGANISATIONAL_OBLIGATION',
    recordRef: id,
    title,
    dueAt,
    kind: 'STATUTORY',
    provisionOrTargetLabel: provision,
    status: 'UPCOMING',
    // These organisation-wide reminders have no assignable worklist record.
    href: id.startsWith('acfr:') ? '/compliance/prudential/reports' : '/compliance/prudential',
    meta: {
      verification: 'SINGLE_SOURCE_22SEP2026',
      organisationLevel: true,
      ...(applies === 'unknown' ? { applicabilityHint: UNKNOWN_APPLICABILITY_HINT } : {}),
    },
  };
}

/** Only the three cited organisational obligations are generated. Unknown
 * applicability deliberately shows, rather than silently hiding a clock. */
export function generateRecurringOrganisationalObligations(
  input: GenerateRecurringObligationsInput,
): ComplianceDeadlineItem[] {
  const items: ComplianceDeadlineItem[] = [];
  const within = (value: string) => value >= input.start && value <= input.end;
  for (
    let year = Number(input.start.slice(0, 4)) - 1;
    year <= Number(input.end.slice(0, 4)) + 1;
    year += 1
  ) {
    if (shouldShow(input.applies.acfr) && within(`${year}-10-31`))
      items.push(
        statutoryItem(
          `acfr:${year}`,
          'Aged Care Financial Report',
          `${year}-10-31`,
          'Aged Care Rules 2025 r 166-310(3)(c); r 166-355(1)',
          input.applies.acfr,
        ),
      );
    if (shouldShow(input.applies.qfr))
      for (const [end, days] of [
        [`${year}-03-31`, 35],
        [`${year}-06-30`, 35],
        [`${year}-09-30`, 35],
        [`${year}-12-31`, 45],
      ] as const) {
        const dueAt = addCalendarDays(end, days);
        if (within(dueAt))
          items.push(
            statutoryItem(
              `qfr:${end}`,
              'Quarterly Financial Report',
              dueAt,
              'Aged Care Rules 2025 r 166-340(7)',
              input.applies.qfr,
            ),
          );
      }
  }
  if (shouldShow(input.applies.rn247)) {
    const rn247 = evaluateRn247MonthlyReportTenantAware(
      input.tenantId,
      input.now,
      input.timezone,
    ).item;
    if (within(rn247.dueAt.slice(0, 10)))
      items.push({
        ...rn247,
        meta: {
          ...rn247.meta,
          verification: 'SINGLE_SOURCE_22SEP2026',
          organisationLevel: true,
          ...(input.applies.rn247 === 'unknown'
            ? { applicabilityHint: UNKNOWN_APPLICABILITY_HINT }
            : {}),
        },
      });
  }
  return items.sort(
    (left, right) => left.dueAt.localeCompare(right.dueAt) || left.id.localeCompare(right.id),
  );
}
